import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  AlertTriangle,
  Shield,
  Clock,
  CheckCircle,
  TrendingUp,
  Users,
  FileText,
  Filter,
  Search,
  RefreshCw,
  Eye,
  MessageSquare,
  Calendar,
  BarChart3,
  Activity,
  AlertCircle,
  Zap,
  Target,
  X,
  Paperclip,
  Download,
  ExternalLink,
  Image,
  FileImage,
  FileType
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

const IncidentDashboard = () => {
  const { user, profile } = useAuth()
  const [incidents, setIncidents] = useState([])
  const [dashboardStats, setDashboardStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [selectedSeverity, setSelectedSeverity] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIncident, setSelectedIncident] = useState(null)
  const [incidentUpdates, setIncidentUpdates] = useState([])
  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [selectedAttachments, setSelectedAttachments] = useState([])
  const [previewAttachment, setPreviewAttachment] = useState(null)
  const [showPreviewModal, setShowPreviewModal] = useState(false)


  const statusOptions = [
    { value: 'all', label: 'All Status', color: 'bg-gray-500' },
    { value: 'submitted', label: 'Submitted', color: 'bg-blue-500' },
    { value: 'triaged', label: 'Triaged', color: 'bg-yellow-500' },
    { value: 'investigating', label: 'Investigating', color: 'bg-orange-500' },
    { value: 'contained', label: 'Contained', color: 'bg-purple-500' },
    { value: 'resolved', label: 'Resolved', color: 'bg-green-500' },
    { value: 'closed', label: 'Closed', color: 'bg-gray-600' }
  ]

  const severityOptions = [
    { value: 'all', label: 'All Severity', color: 'bg-gray-500' },
    { value: 'critical', label: 'Critical', color: 'bg-red-600' },
    { value: 'high', label: 'High', color: 'bg-orange-600' },
    { value: 'medium', label: 'Medium', color: 'bg-yellow-600' },
    { value: 'low', label: 'Low', color: 'bg-green-600' }
  ]

  const incidentTypes = {
    'malware': 'Malware/Virus',
    'phishing': 'Phishing/Email Attack',
    'data-breach': 'Data Breach',
    'unauthorized-access': 'Unauthorized Access',
    'network-intrusion': 'Network Intrusion',
    'system-compromise': 'System Compromise',
    'social-engineering': 'Social Engineering',
    'other': 'Other Security Incident'
  }

  useEffect(() => {
    fetchDashboardData()
    
    // Set up real-time subscription
    const incidentsSubscription = supabase
      .channel('incidents-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'incidents' },
        (payload) => {
          // Check if user is admin
          const isAdmin = profile?.user_type === 'it_administrator' || 
                         profile?.user_type === 'security_officer' ||
                         profile?.role === 'Administrator'
          
          // For non-admin users, only refresh if the incident involves them
          if (isAdmin || 
              payload.new?.reported_by === user?.id || 
              payload.old?.reported_by === user?.id) {
            fetchDashboardData()
          }
        }
      )
      .subscribe()

    return () => {
      incidentsSubscription.unsubscribe()
    }
  }, [profile, user])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      
      // Check if user is admin
      const isAdmin = profile?.user_type === 'it_administrator' || 
                     profile?.user_type === 'security_officer' ||
                     profile?.role === 'Administrator'
      
      // Build query based on user role
      let query = supabase
        .from('incidents')
        .select(`
          *,
          reported_by_user:reported_by(name, email),
          assigned_to_user:assigned_to(name, email)
        `)
      
      // Filter incidents for non-admin users
      if (!isAdmin && user?.id) {
        query = query.eq('reported_by', user.id)
      }
      
      const { data: incidentsData, error: incidentsError } = await query
        .order('created_at', { ascending: false })
      
      if (incidentsError) throw incidentsError
      
      // Fetch dashboard statistics
      let statsData = null
      try {
        // For non-admin users, skip the dashboard stats view and calculate manually
        // since the view likely shows all incidents, not user-filtered ones
        if (isAdmin) {
          const { data, error: statsError } = await supabase
            .from('incident_dashboard_stats')
            .select('*')
            .single()
          
          if (!statsError) {
            statsData = data
          }
        }
      } catch (error) {
        console.log('Dashboard stats view not available yet, calculating manually')
        // Calculate basic stats manually if view doesn't exist
        if (incidentsData && incidentsData.length > 0) {
          statsData = {
            total_incidents: incidentsData.length,
            pending_incidents: incidentsData.filter(i => 
              i.status === 'submitted' || i.status === 'reported'
            ).length,
            active_incidents: incidentsData.filter(i => 
              ['triaged', 'investigating', 'contained'].includes(i.status)
            ).length,
            resolved_incidents: incidentsData.filter(i => 
              ['resolved', 'closed'].includes(i.status)
            ).length,
            critical_incidents: incidentsData.filter(i => 
              i.severity === 'critical'
            ).length,
            high_incidents: incidentsData.filter(i => 
              i.severity === 'high'
            ).length,
            incidents_last_24h: incidentsData.filter(i => {
              const created = new Date(i.created_at)
              const now = new Date()
              return (now - created) < 24 * 60 * 60 * 1000
            }).length,
            incidents_last_7d: incidentsData.filter(i => {
              const created = new Date(i.created_at)
              const now = new Date()
              return (now - created) < 7 * 24 * 60 * 60 * 1000
            }).length
          }
        }
      }
      
      setIncidents(incidentsData || [])
      setDashboardStats(statsData)
      

      
    } catch (error) {
      console.error('Error fetching dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }



  const fetchIncidentUpdates = async (incidentId) => {
    try {
      const { data, error } = await supabase
        .from('incident_updates')
        .select(`
          *,
          updated_by_user:updated_by(name, email)
        `)
        .eq('incident_id', incidentId)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      setIncidentUpdates(data || [])
    } catch (error) {
      console.error('Error fetching incident updates:', error)
    }
  }

  const updateIncidentStatus = async (incidentId, newStatus, message = '') => {
    try {
      const { error } = await supabase.rpc('update_incident_status', {
        p_incident_id: incidentId,
        p_new_status: newStatus,
        p_update_message: message || null
      })
      
      if (error) throw error
      
      // Refresh data
      await fetchDashboardData()
      if (selectedIncident?.id === incidentId) {
        await fetchIncidentUpdates(incidentId)
      }
      
    } catch (error) {
      console.error('Error updating incident status:', error)
      alert('Failed to update incident status')
    }
  }

  const filteredIncidents = incidents.filter(incident => {
    const matchesStatus = selectedStatus === 'all' || incident.status === selectedStatus
    const matchesSeverity = selectedSeverity === 'all' || incident.severity === selectedSeverity
    const matchesSearch = searchTerm === '' || 
      incident.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.ticket_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      incident.reporter_name.toLowerCase().includes(searchTerm.toLowerCase())
    
    return matchesStatus && matchesSeverity && matchesSearch
  })

  const getStatusColor = (status) => {
    const statusColors = {
      'submitted': 'bg-blue-100 text-blue-800',
      'triaged': 'bg-yellow-100 text-yellow-800',
      'investigating': 'bg-orange-100 text-orange-800',
      'contained': 'bg-purple-100 text-purple-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    }
    return statusColors[status] || 'bg-gray-100 text-gray-800'
  }

  const getSeverityColor = (severity) => {
    const severityColors = {
      'critical': 'bg-red-100 text-red-800 border-red-200',
      'high': 'bg-orange-100 text-orange-800 border-orange-200',
      'medium': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'low': 'bg-green-100 text-green-800 border-green-200'
    }
    return severityColors[severity] || 'bg-gray-100 text-gray-800 border-gray-200'
  }

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-PH', {
      timeZone: 'Asia/Manila',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTimeAgo = (dateString) => {
    const now = new Date()
    const date = new Date(dateString)
    const diffInMinutes = Math.floor((now - date) / (1000 * 60))
    
    if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`
    } else if (diffInMinutes < 1440) {
      return `${Math.floor(diffInMinutes / 60)}h ago`
    } else {
      return `${Math.floor(diffInMinutes / 1440)}d ago`
    }
  }

  const viewAttachments = (incident) => {
    setSelectedAttachments(incident.attachment_paths || [])
    setShowAttachmentModal(true)
  }

  const downloadAttachment = async (attachmentPath) => {
    try {
      // Check if the storage bucket exists first
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets()
      const bucketExists = buckets?.some(bucket => bucket.name === 'incident-attachments')
      
      if (!bucketExists) {
        alert('Storage bucket "incident-attachments" does not exist. Please contact your administrator to set up the storage infrastructure.')
        return
      }
      
      const { data, error } = await supabase.storage
        .from('incident-attachments')
        .createSignedUrl(attachmentPath, 3600) // 1 hour expiry
      
      if (error) {
        console.error('Error downloading attachment:', error)
        if (error.message.includes('Object not found')) {
          alert(`Attachment file "${getAttachmentFileName(attachmentPath)}" was not found in storage. The file may have been deleted or moved.`)
        } else {
          alert(`Failed to download attachment: ${error.message}`)
        }
        return
      }
      
      // Create a temporary link and trigger download
      const link = document.createElement('a')
      link.href = data.signedUrl
      link.download = getAttachmentFileName(attachmentPath)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error('Error downloading attachment:', error)
      alert(`Failed to download attachment: ${error.message || 'Unknown error'}`)
    }
  }

  const getAttachmentFileName = (path) => {
    return path.split('/').pop() || 'Unknown file'
  }

  const getFileType = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
      return 'image'
    } else if (['pdf'].includes(extension)) {
      return 'pdf'
    } else if (['txt', 'log', 'csv'].includes(extension)) {
      return 'text'
    } else if (['json'].includes(extension)) {
      return 'json'
    }
    return 'other'
  }

  const getFileIcon = (fileName) => {
    const fileType = getFileType(fileName)
    switch (fileType) {
      case 'image':
        return <FileImage className="w-5 h-5 text-blue-500" />
      case 'pdf':
        return <FileText className="w-5 h-5 text-red-500" />
      case 'text':
      case 'json':
        return <FileType className="w-5 h-5 text-green-500" />
      default:
        return <FileText className="w-5 h-5 text-gray-500" />
    }
  }

  const previewAttachmentFile = async (attachmentPath) => {
    try {
      // Check if the storage bucket exists first
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets()
      const bucketExists = buckets?.some(bucket => bucket.name === 'incident-attachments')
      
      if (!bucketExists) {
        alert('Storage bucket "incident-attachments" does not exist. Please contact your administrator to set up the storage infrastructure.')
        return
      }
      
      const { data, error } = await supabase.storage
        .from('incident-attachments')
        .createSignedUrl(attachmentPath, 3600) // 1 hour expiry
      
      if (error) {
        console.error('Error creating preview URL:', error)
        if (error.message.includes('Object not found')) {
          alert(`Attachment file "${getAttachmentFileName(attachmentPath)}" was not found in storage. The file may have been deleted or moved.`)
        } else {
          alert(`Failed to preview attachment: ${error.message}`)
        }
        return
      }
      
      const fileName = getAttachmentFileName(attachmentPath)
      const fileType = getFileType(fileName)
      
      setPreviewAttachment({
        path: attachmentPath,
        fileName,
        fileType,
        url: data.signedUrl
      })
      setShowPreviewModal(true)
    } catch (error) {
      console.error('Error creating preview URL:', error)
      alert(`Failed to preview attachment: ${error.message || 'Unknown error'}`)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
        <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{maxWidth: '130rem'}}>
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center space-x-2">
              <RefreshCw className="w-6 h-6 animate-spin text-cyber-600" />
              <span className="text-lg text-gov-600">Loading incident dashboard...</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gov-50 to-cyber-50 py-8">
      <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{maxWidth: '145rem'}}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-cyber-600 rounded-full flex items-center justify-center">
              <Activity className="w-8 h-8 text-white" />
            </div>
          </div>
          <h1 className="text-4xl font-display font-bold text-gov-800 mb-4">
            Incident Response Dashboard
          </h1>
          <p className="text-xl text-gov-600 max-w-3xl mx-auto">
            Real-time monitoring and management of cybersecurity incidents
          </p>
          {/* User Role Indicator */}
          <div className="mt-4 flex justify-center">
            {profile?.user_type === 'it_administrator' || 
             profile?.user_type === 'security_officer' ||
             profile?.role === 'Administrator' ? (
              <div className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium">
                <Shield className="w-4 h-4 inline mr-2" />
                Administrator View - Showing All Incidents
              </div>
            ) : (
              <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium">
                <Users className="w-4 h-4 inline mr-2" />
                User View - Showing Your Incidents Only
              </div>
            )}
          </div>
        </motion.div>

        {/* Statistics Cards */}
        {dashboardStats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <div className="card bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Incidents</p>
                  <p className="text-3xl font-bold">{dashboardStats.total_incidents || 0}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-200" />
              </div>
            </div>
            
            <div className="card bg-gradient-to-r from-yellow-500 to-yellow-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-yellow-100 text-sm">Pending</p>
                  <p className="text-3xl font-bold">{dashboardStats.pending_incidents || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-200" />
              </div>
            </div>
            
            <div className="card bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Active</p>
                  <p className="text-3xl font-bold">{dashboardStats.active_incidents || 0}</p>
                </div>
                <Zap className="w-8 h-8 text-orange-200" />
              </div>
            </div>
            
            <div className="card bg-gradient-to-r from-green-500 to-green-600 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Resolved</p>
                  <p className="text-3xl font-bold">{dashboardStats.resolved_incidents || 0}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-200" />
              </div>
            </div>
          </motion.div>
        )}



        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gov-400" />
                <input
                  type="text"
                  placeholder="Search incidents..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="px-4 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                {statusOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              
              <select
                value={selectedSeverity}
                onChange={(e) => setSelectedSeverity(e.target.value)}
                className="px-4 py-2 border border-gov-300 rounded-lg focus:ring-2 focus:ring-cyber-500 focus:border-transparent"
              >
                {severityOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            
            <button
              onClick={fetchDashboardData}
              className="btn-secondary flex items-center space-x-2"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </motion.div>

        {/* Incidents Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card overflow-hidden"
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gov-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Severity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Reporter
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Last Updated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Attachments
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gov-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gov-200">
                {filteredIncidents.map((incident) => (
                  <tr key={incident.id} className="hover:bg-gov-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gov-900">
                        {incident.ticket_number}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gov-900 max-w-[200px] truncate" title={incident.title}>
                        {incident.title}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gov-600">
                        {incidentTypes[incident.incident_type] || incident.incident_type}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getSeverityColor(incident.severity)}`}>
                        {incident.severity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(incident.status)}`}>
                        {incident.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gov-600 max-w-[120px] truncate" title={incident.location}>
                        {incident.location || 'Not specified'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gov-900">{incident.reporter_name}</div>
                      <div className="text-xs text-gov-500">{incident.department}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gov-900">{getTimeAgo(incident.created_at)}</div>
                      <div className="text-xs text-gov-500">{formatDateTime(incident.created_at)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gov-900">{getTimeAgo(incident.updated_at)}</div>
                      <div className="text-xs text-gov-500">{formatDateTime(incident.updated_at)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gov-600 max-w-[200px] truncate" title={incident.description}>
                        {incident.description || 'No description provided'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {incident.attachment_paths && incident.attachment_paths.length > 0 ? (
                        <div className="flex items-center space-x-2">
                          <Paperclip className="w-4 h-4 text-gov-500" />
                          <span className="text-sm text-gov-600">
                            {incident.attachment_paths.length} file{incident.attachment_paths.length > 1 ? 's' : ''}
                          </span>
                          <div className="flex space-x-1">
                            {incident.attachment_paths.slice(0, 3).map((path, index) => (
                              <button
                                key={index}
                                onClick={() => previewAttachmentFile(path)}
                                className="text-cyber-600 hover:text-cyber-900 p-1 rounded hover:bg-cyber-50"
                                title={`Preview ${getAttachmentFileName(path)}`}
                              >
                                {getFileIcon(getAttachmentFileName(path))}
                              </button>
                            ))}
                            {incident.attachment_paths.length > 3 && (
                              <button
                                onClick={() => viewAttachments(incident)}
                                className="text-gov-600 hover:text-gov-800 text-xs px-2 py-1 rounded hover:bg-gov-50"
                              >
                                +{incident.attachment_paths.length - 3}
                              </button>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gov-400">No attachments</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedIncident(incident)
                          fetchIncidentUpdates(incident.id)
                        }}
                        className="text-cyber-600 hover:text-cyber-900 mr-3"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {filteredIncidents.length === 0 && (
              <div className="text-center py-12">
                <AlertTriangle className="w-12 h-12 text-gov-400 mx-auto mb-4" />
                <p className="text-gov-600">No incidents found matching your criteria.</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Incident Detail Modal */}
        {selectedIncident && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gov-800">
                    {selectedIncident.ticket_number}
                  </h2>
                  <button
                    onClick={() => setSelectedIncident(null)}
                    className="text-gov-400 hover:text-gov-600"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                {/* Incident details content would go here */}
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="font-semibold text-gov-800 mb-2">Incident Details</h3>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Title:</span> {selectedIncident.title}</div>
                        <div><span className="font-medium">Type:</span> {incidentTypes[selectedIncident.incident_type]}</div>
                        <div><span className="font-medium">Severity:</span> 
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getSeverityColor(selectedIncident.severity)}`}>
                            {selectedIncident.severity}
                          </span>
                        </div>
                        <div><span className="font-medium">Status:</span> 
                          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(selectedIncident.status)}`}>
                            {selectedIncident.status}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="font-semibold text-gov-800 mb-2">Reporter Information</h3>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Name:</span> {selectedIncident.reporter_name}</div>
                        <div><span className="font-medium">Email:</span> {selectedIncident.reporter_email}</div>
                        <div><span className="font-medium">Department:</span> {selectedIncident.department}</div>
                        <div><span className="font-medium">Agency:</span> {selectedIncident.agency}</div>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold text-gov-800 mb-2">Description</h3>
                    <p className="text-sm text-gov-600 bg-gov-50 p-4 rounded-lg">
                      {selectedIncident.description}
                    </p>
                  </div>
                  
                  {/* Attachments Section */}
                  <div>
                    <h3 className="font-semibold text-gov-800 mb-2">Attachments</h3>
                    {selectedIncident.attachment_paths && selectedIncident.attachment_paths.length > 0 ? (
                      <div className="space-y-2">
                        {selectedIncident.attachment_paths.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gov-50 rounded-lg border">
                            <div className="flex items-center space-x-3">
                              {getFileIcon(getAttachmentFileName(attachment))}
                              <div>
                                <p className="text-sm font-medium text-gov-900">
                                  {getAttachmentFileName(attachment)}
                                </p>
                                <p className="text-xs text-gov-500">
                                  {attachment}
                                </p>
                              </div>
                            </div>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => previewAttachmentFile(attachment)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                                title="Preview attachment"
                              >
                                <Eye className="w-3 h-3" />
                                <span>Preview</span>
                              </button>
                              <button
                                onClick={() => downloadAttachment(attachment)}
                                className="flex items-center space-x-1 px-2 py-1 text-xs bg-cyber-600 text-white rounded hover:bg-cyber-700 transition-colors"
                              >
                                <Download className="w-3 h-3" />
                                <span>Download</span>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gov-500 bg-gov-50 p-4 rounded-lg">
                        No attachments available for this incident.
                      </p>
                    )}
                  </div>
                  
                  {/* Status Update Section */}
                  <div>
                    <h3 className="font-semibold text-gov-800 mb-4">Update Status</h3>
                    <div className="flex flex-wrap gap-2">
                      {statusOptions.slice(1).map(status => (
                        <button
                          key={status.value}
                          onClick={() => updateIncidentStatus(selectedIncident.id, status.value)}
                          disabled={selectedIncident.status === status.value}
                          className={`px-3 py-1 text-sm rounded-lg ${
                            selectedIncident.status === status.value
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-cyber-100 text-cyber-800 hover:bg-cyber-200'
                          }`}
                        >
                          {status.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Updates Timeline */}
                  <div>
                    <h3 className="font-semibold text-gov-800 mb-4">Updates Timeline</h3>
                    <div className="space-y-3">
                      {incidentUpdates.map((update, index) => (
                        <div key={update.id} className="flex items-start space-x-3">
                          <div className="w-2 h-2 bg-cyber-600 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <div className="text-sm">
                              <span className="font-medium">Status changed to {update.new_status}</span>
                              {update.update_message && (
                                <span className="text-gov-600"> - {update.update_message}</span>
                              )}
                            </div>
                            <div className="text-xs text-gov-500">
                              {formatDateTime(update.created_at)} by {update.updated_by_user?.name || 'System'}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Attachment Modal */}
      {showAttachmentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Incident Attachments</h3>
              <button
                onClick={() => setShowAttachmentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedAttachments.length > 0 ? (
                <div className="space-y-3">
                  {selectedAttachments.map((attachment, index) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {getAttachmentFileName(attachment)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {attachment}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => downloadAttachment(attachment)}
                        className="flex items-center space-x-2 px-3 py-2 text-sm bg-cyber-600 text-white rounded-md hover:bg-cyber-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        <span>Download</span>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No attachments found for this incident.</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={() => setShowAttachmentModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreviewModal && previewAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden"
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Preview: {previewAttachment.fileName}</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {previewAttachment.fileType === 'image' ? (
                <div className="text-center">
                  <img 
                    src={previewAttachment.url} 
                    alt={previewAttachment.fileName}
                    className="max-w-full max-h-[60vh] object-contain mx-auto rounded-lg shadow-lg"
                  />
                </div>
              ) : previewAttachment.fileType === 'pdf' ? (
                <div className="text-center">
                  <iframe
                    src={previewAttachment.url}
                    className="w-full h-[60vh] border rounded-lg"
                    title={previewAttachment.fileName}
                  />
                </div>
              ) : previewAttachment.fileType === 'text' || previewAttachment.fileType === 'json' ? (
                <div className="text-center">
                  <iframe
                    src={previewAttachment.url}
                    className="w-full h-[60vh] border rounded-lg bg-gray-50"
                    title={previewAttachment.fileName}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="mb-4 flex justify-center">
                    {getFileIcon(previewAttachment.fileName)}
                  </div>
                  <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                  <button
                    onClick={() => downloadAttachment(previewAttachment.path)}
                    className="flex items-center space-x-2 px-4 py-2 bg-cyber-600 text-white rounded-md hover:bg-cyber-700 transition-colors mx-auto"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download to view</span>
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center p-6 border-t border-gray-200">
              <div className="text-sm text-gray-500">
                File: {previewAttachment.fileName}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => downloadAttachment(previewAttachment.path)}
                  className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Download</span>
                </button>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default IncidentDashboard